import {
  logError,
  logWarn,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { GLOBAL } from 'saddlebag-browser';

import {
  getSourceDomain,
  getResourceTimingsForFailedUrl,
  isChunkLoadFailure,
  logUnhandled,
  shouldLogError,
} from './utils';

import type { LogErrorType } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

const $window = GLOBAL.getWindow();
const $document = GLOBAL.getDocument();
const $navigator = GLOBAL.getNavigator();

let sentErrorCount = 0;
let errorMessages: string[] = [];

const logSendThreshold = 100;

type OnError = {
  component: string;
  error: Error;
  message: string;
  source?: string;
  resourceTimings?: PerformanceEntry[];
  chunkLoadErrorType?: string;
  visibility?: boolean;
  userAgent?: string;
};

const onError = ({
  chunkLoadErrorType,
  component,
  error,
  message,
  resourceTimings,
  source,
  userAgent,
  visibility,
}: OnError) => {
  /*
    To prevent noisy errors clogging our logs we limit at 1 log per error and 100 total logs per user. This should give us visibility of the breadth of errors encountered, without noisy third party scripts overwhelming us.
  */
  const isBelowThreshold = sentErrorCount < logSendThreshold;
  const isNewError = !errorMessages.includes(message);

  if (!isBelowThreshold || !isNewError) return;

  const fields: LogErrorType = {
    component,
    message,
    error,
    visibility: visibility !== undefined ? visibility.toString() : 'undefined',
    // Only include it if it exists
    ...(source && { source }),
    ...(chunkLoadErrorType && { chunkLoadErrorType }),
    ...(userAgent && { userAgent }),
    ...(resourceTimings && { data: JSON.stringify(resourceTimings) }),
  };

  logError(fields);
  sentErrorCount += 1;
  errorMessages.push(message);
};

/*
  This function returns true in the cases where we want to prevent sending errors to new relic,
  and false for cases where we do want to propagate the error
*/
export const newRelicErrorHandler = (err: Error | string) => {
  // Path for if the err is a plain string
  if (typeof err === 'string') {
    // Until we understand more about when this could ever be called with a string just allow the error to be logged
    return false;
  }

  // Path for if the err is an Error
  const { message, stack = '' } = err;

  try {
    const sourceDomain = getSourceDomain(stack);

    // Match the filtering done in our window.onerror and window.onunhandledrejection handlers.
    if (!shouldLogError({ message, sourceDomain, stack })) {
      return true;
    }

    return false;
  } catch {
    logWarn({
      component: 'newRelicErrorHandler',
      message: 'Failed to parse an error in newRelicErrorHandler',
      stack,
    });

    return false;
  }
};

/*
There are two standard handlers that browsers provide as a last resort for catching unhandled errors: window.onerror and window.onunhandledrejection.

These can catch errors from anywhere; our code; browser plugins; or third party scripts running on our page.

Over time our experience has led us to adopt aggressive filtering of these errors. We suppress or segment any errors we do not believe to be from our code. These third party errors historically repeatedly trigger alerts while being overwhelmingly unactionable, and their high constant volume reduces visibility of errors we can action. We accept the risk trade off of reducing visibility of these.

This relies on the expectation that any failures we _do_ care about in third party scripts will be picked up by other metrics - for example a failure in Google's One Tap Login code being picked up by 'successful login' metrics.

The New Relic Browser agent also adds its own window error handlers, duplicates of the standard ones that we can only interact with via the newrelic.setErrorHandler API. We have a product request in to be able to completely disable these, as we find them less customisable in adding extra fields, less effective (suppressions do not suppress 100% of errors), and less suited to our error monitoring (no 'component' field present on errors). We cannot simply suppress _all_ errors in newrelic.setErrorHandler as it also acts as global handler for errors received from logError - turning off all errors would turn of *everything*, not just New Relic's window error handlers. While waiting on this product request we duplicate all suppressions applied to window.onerror and window.onunhandledrejection in this handler.
*/
export const setupErrorHandling = () => {
  sentErrorCount = 0;
  errorMessages = [];

  if ($window.newrelic) {
    /*
    https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/seterrorhandler/
    https://github.com/newrelic/newrelic-browser-agent/blob/129f3b089170079b3970dfe58cbc01564ccabd98/src/loaders/agent-base.js#L71

    By default since v1220 New Relic adds its own onerror and onunhandledrejection handlers. It provides the `setErrorHandler` method to allow errors to be ignored.

    IMPORTANT: This error handler is called for _all_ errors, not just unhandled ones, so cannot simply be turned off; doing so would stop our logError from working.
    */
    $window.newrelic.setErrorHandler(newRelicErrorHandler);
  }

  $window.onerror = (
    mess: string,
    source: string,
    lineno: number,
    colno: number,
    error: Error | undefined,
  ) => {
    /*
    https://developer.mozilla.org/en-US/docs/Web/API/Window/error_event

    window.onerror always includes the source and message. We use this to filter our errors believed to come from code outside our application, as this is beyond our control so unactionable.

    However, as the New Relic Browser Agent intercepts errors and so adds a 'nrWrapper' entry as the final frame on many stack traces, which 'source' is determined from by browsers, we cannot trust this source property, so must parse it ourselves using 'getSourceDomain'.
    */
    const component = 'windowOnError';
    const message = mess.toString();
    const stack = (error && error.stack) || '';

    try {
      const sourceDomain = getSourceDomain(stack);

      // Don't log cross-domain script errors
      // Don't log errors which don't come from our scripts
      if (shouldLogError({ message, sourceDomain, stack })) {
        onError({
          component,
          error: error || new Error('window.onerror'),
          message,
          source: sourceDomain,
        });
      } else {
        logUnhandled({
          component,
          message: `Unhandled window error believed to be unactionable: ${message}`,
          source: sourceDomain || 'undefined source',
          stack,
        });
      }
    } catch {
      logWarn({
        component,
        message: 'Error evaluating an error in window.onerror',
        stack,
      });
    }
  };

  $window.onunhandledrejection = (rejection: PromiseRejectionEvent) => {
    /*
    https://developer.mozilla.org/en-US/docs/Web/API/Window/unhandledrejection_event

    window.onunhandledrejection forwards on the value given to Promise.reject. Compared to window.onerror this is less dependable to work with, as there is nothing to enforce specific fields will exist within the rejection reason.
    */
    let component = 'windowOnUnhandledRejection';

    // If there is no reason it is definitely unactionable, so we immediately suppress it. We do not want to clutter our error or warning tables, but do want to retain daily visibility.
    if (!rejection.reason) {
      return logUnhandled({
        component,
        message: 'Unhandled promise rejection with undefined rejection reason',
        source: 'undefined source',
      });
    }

    // If the rejection reason doesn't have a stack trace, it's a non-Error value
    // (e.g. a plain object, string, or DOMException from third-party scripts like FedCM/GSI).
    // This is unactionable, so we segment it like other unactionable rejections.
    const { stack } = rejection.reason;
    if (typeof stack !== 'string') {
      return logUnhandled({
        component,
        message:
          'Unhandled promise rejection with non-Error reason (no stack trace to determine source)',
        source: 'undefined source',
        reasonType: typeof rejection.reason,
        reasonString: String(rejection.reason).substring(0, 200),
      });
    }

    try {
      const sourceDomain = getSourceDomain(stack);

      if (shouldLogError({ message: rejection.reason.message, sourceDomain, stack })) {
        let resourceTimings;
        let chunkLoadErrorType;

        if (isChunkLoadFailure(rejection.reason.name, rejection.reason.code)) {
          component = 'ChunkLoadError';
          // If a chunk load failure then webpack should also add a type here: https://github.com/webpack/webpack/blob/60daca54105f89eee45e118fd0bbc820730724ee/lib/web/JsonpChunkLoadingRuntimeModule.js#L191
          // 'missing': file exists but the content is empt (requesting the file was successful)
          // 'error': file not exists (not found), or a network issue in requesting the file (requesting the file was failed)
          chunkLoadErrorType = rejection.reason?.type || 'no type specified';
          // request == URL of the given chunk loading failure
          const requestUrl = rejection.reason?.request;
          resourceTimings = getResourceTimingsForFailedUrl(requestUrl);
        }

        return onError({
          component,
          error: rejection.reason || new Error('window.onunhandledrejection'),
          message: rejection.reason.message,
          source: sourceDomain,
          resourceTimings,
          chunkLoadErrorType,
          visibility: $document.hidden,
          userAgent: $navigator.userAgent,
        });
      }

      // Again we segment into a separate table, as with `!rejection.reason` segmentation
      return logUnhandled({
        component,
        message: 'Unhandled promise rejection with third party source',
        stack,
        source: sourceDomain || 'undefined source',
      });
    } catch {
      return logWarn({
        component,
        message: 'Unhandled promise rejection with undetermined source',
        stack,
      });
    }
  };
};
